import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { apiAuth } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge, PIPELINE_INVESTORS } from "@/components/admin/StatusBadge";
import { StatusChangeModal } from "@/components/admin/StatusChangeModal";
import NotesPanel from "@/components/admin/NotesPanel";
import TagsPanel from "@/components/admin/TagsPanel";
import TimelineList from "@/components/admin/TimelineList";

const TABS = [
  ["overview", "Übersicht"],
  ["thesis", "Investment Thesis"],
  ["prefs", "Präferenzen"],
  ["meetings", "Meetings"],
  ["startups", "Startups vorgestellt"],
  ["notes", "Notes"],
  ["tags", "Tags"],
  ["relationship", "Beziehung"],
  ["timeline", "Timeline"],
  ["consent", "Consent"],
];

const Pill = ({ children, accent = "bg-roseMilk" }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs text-cocoa ${accent}`}>
    {children}
  </span>
);

export default function AdminInvestorDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [statusOpen, setStatusOpen] = useState(false);
  const [relSaving, setRelSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await apiAuth.get(`/admin/investors/${id}`);
      setData(data);
    } catch (_e) { setError("Investor:in nicht gefunden."); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (error) return <p className="text-sm text-coral">{error}</p>;
  if (!data) return <p className="text-sm text-warmGrey">Lade …</p>;

  const { investor, contact, consents } = data;

  const saveRelationship = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const payload = {
      fatma_confidence_score: form.get("fcs") ? Number(form.get("fcs")) : null,
      relationship_strength: form.get("rs") || null,
      trust_level: form.get("tl") || null,
      discretion_level: form.get("dl") || null,
      intro_readiness: form.get("ir") || null,
      personal_note: form.get("pn") || null,
    };
    Object.keys(payload).forEach((k) => { if (payload[k] == null) delete payload[k]; });
    setRelSaving(true);
    try {
      await apiAuth.patch(`/admin/investors/${id}/relationship`, payload);
      await load();
    } finally { setRelSaving(false); }
  };

  return (
    <div data-testid="page-investor-detail" className="flex flex-col gap-6">
      <Link to="/admin/investors" className="text-xs text-warmGrey hover:text-raspberry inline-flex items-center gap-1">
        <ArrowLeft size={12} /> Zurück zur Liste
      </Link>

      <header className="bg-white border border-sandBeige rounded-3xl p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <h1 className="font-serif text-3xl text-cocoa">{contact ? `${contact.first_name} ${contact.last_name}` : "—"}</h1>
          <p className="text-sm text-warmGrey mt-1">{investor.organization_name}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <StatusBadge value={investor.status} />
            <span className="text-xs text-warmGrey">{contact?.city}, {contact?.country}</span>
          </div>
        </div>
        <button type="button" onClick={() => setStatusOpen(true)} className="wit-btn-secondary text-xs" data-testid="btn-change-status">
          Status ändern
        </button>
      </header>

      <nav className="flex flex-wrap gap-2" data-testid="investor-tabs">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={`wit-pill text-xs ${tab === key ? "bg-raspberry text-white border-raspberry" : "bg-white border-sandBeige text-cocoa"}`}
            data-testid={`tab-${key}`}
          >
            {label}
          </button>
        ))}
      </nav>

      <section className="bg-white border border-sandBeige rounded-3xl p-6">
        {tab === "overview" && (
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm" data-testid="tab-content-overview">
            <Row label="Name" value={contact ? `${contact.first_name} ${contact.last_name}` : "—"} />
            <Row label="E-Mail" value={contact?.email} />
            <Row label="LinkedIn" value={contact?.linkedin_url} link />
            <Row label="Telefon" value={contact?.phone} />
            <Row label="Rolle" value={contact?.role} />
            <Row label="Standort" value={contact ? `${contact.city}, ${contact.country}` : "—"} />
            <Row label="Zeitzone" value={contact?.timezone} />
            <Row label="Sprache" value={(contact?.language || investor.language || "").toUpperCase()} />
            <Row label="Organisation" value={investor.organization_name} />
            <Row label="Website" value={investor.organization_website} link />
            <Row label="Submitted" value={investor.created_at ? new Date(investor.created_at).toLocaleString("de-DE") : "—"} />
            <Row label="Status" value={investor.status} />
          </dl>
        )}

        {tab === "thesis" && (
          <div className="flex flex-col gap-5" data-testid="tab-content-thesis">
            <Section title="Investor-Typen">
              {(investor.investor_types || []).map((t) => <Pill key={t} accent="bg-skyMist">{t}</Pill>)}
            </Section>
            <Section title="Stage Focus">
              {(investor.stage_focus || []).map((t) => <Pill key={t} accent="bg-lavenderFog">{t}</Pill>)}
            </Section>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-butterCream/50 rounded-2xl p-4">
                <p className="wit-eyebrow text-cocoa/80">Ticket Min</p>
                <p className="font-serif text-2xl text-cocoa mt-1">{Math.round(investor.ticket_min/1000)}k {investor.currency}</p>
              </div>
              <div className="bg-butterCream/50 rounded-2xl p-4">
                <p className="wit-eyebrow text-cocoa/80">Ticket Max</p>
                <p className="font-serif text-2xl text-cocoa mt-1">{Math.round(investor.ticket_max/1000)}k {investor.currency}</p>
              </div>
              <div className="bg-butterCream/50 rounded-2xl p-4">
                <p className="wit-eyebrow text-cocoa/80">Position</p>
                <p className="text-sm text-cocoa mt-2">
                  {investor.is_lead && "Lead · "} {investor.is_follow && "Follow · "}
                  {investor.follow_on_possible && "Follow-on · "} {investor.co_invest_possible && "Co-invest"}
                </p>
              </div>
            </div>
            <Section title="Sektoren bevorzugt">
              {(investor.sectors_preferred || []).map((t) => <Pill key={t} accent="bg-sageMint">{t}</Pill>)}
            </Section>
            {investor.sectors_open?.length > 0 && (
              <Section title="Sektoren bei Empfehlung">
                {investor.sectors_open.map((t) => <Pill key={t} accent="bg-ivory">{t}</Pill>)}
              </Section>
            )}
            {investor.sectors_excluded?.length > 0 && (
              <Section title="Sektoren ausgeschlossen">
                {investor.sectors_excluded.map((t) => <Pill key={t} accent="bg-coral/20">{t}</Pill>)}
              </Section>
            )}
            <Section title="Regionen-Fokus">
              {(investor.regions_focus || []).map((t) => <Pill key={t} accent="bg-skyMist">{t}</Pill>)}
            </Section>
            <Section title="Business Models">
              {(investor.business_models || []).map((t) => <Pill key={t} accent="bg-roseMilk">{t}</Pill>)}
            </Section>
            <Section title="Traction Requirements">
              {(investor.traction_requirements || []).map((t) => <Pill key={t} accent="bg-lavenderFog">{t}</Pill>)}
            </Section>
            <Section title="Value Add">
              {(investor.value_add || []).map((t) => <Pill key={t} accent="bg-butterCream">{t}</Pill>)}
            </Section>
            <Section title="Diversity-Interesse">
              {Object.entries(investor.diversity_interests || {}).filter(([_, v]) => v).map(([k]) => (
                <Pill key={k} accent="bg-roseMilk">{k}</Pill>
              ))}
            </Section>
            {investor.thesis_text && (
              <div className="bg-lavenderFog/40 rounded-2xl p-5">
                <p className="wit-eyebrow text-cocoa/80 mb-2">Investment Thesis</p>
                <p className="text-sm text-cocoa whitespace-pre-wrap leading-relaxed">{investor.thesis_text}</p>
                <p className="text-xs text-warmGrey mt-3">Strukturierte Parsing folgt in Phase 8.</p>
              </div>
            )}
          </div>
        )}

        {tab === "prefs" && (
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm" data-testid="tab-content-prefs">
            <Row label="Dealflow-Frequenz" value={investor.dealflow_frequency} />
            <Row label="Curated only" value={investor.curated_only ? "Ja" : "Nein"} />
            <Row label="Early Signals" value={investor.open_to_early_signals ? "Ja" : "Nein"} />
            <Row label="Teaser-Präferenz" value={investor.teaser_preference} />
            <Row label="Will Fatma-Call vor Reveal" value={investor.wants_fatma_call_before_reveal ? "Ja" : "Nein"} />
            <Row label="Kanal" value={investor.comms_preference} />
            <Row label="Monthly Digest" value={investor.comms_monthly_digest ? "Ja" : "Nein"} />
            <Row label="Sofort-Alerts" value={investor.comms_immediate_alerts ? "Ja" : "Nein"} />
          </dl>
        )}

        {tab === "meetings" && (
          <p className="text-sm text-warmGrey" data-testid="tab-content-meetings">
            Termine kommen in Phase 10 (Booking Engine).
          </p>
        )}

        {tab === "startups" && (
          <p className="text-sm text-warmGrey" data-testid="tab-content-startups-stub">
            Match-Workflow kommt in Phase 8.
          </p>
        )}

        {tab === "notes" && <NotesPanel entityType="investor" entityId={id} currentUserId={user?.id} />}
        {tab === "tags" && <TagsPanel entityType="investor" entityId={id} initialTags={investor.tags || []} />}
        {tab === "timeline" && <TimelineList entityType="investor" entityId={id} />}

        {tab === "relationship" && (
          <form onSubmit={saveRelationship} className="grid sm:grid-cols-2 gap-4" data-testid="tab-content-relationship">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-cocoa">Fatma Confidence (1–5)</span>
              <input className="wit-input" type="number" min="1" max="5"
                name="fcs" defaultValue={investor.fatma_confidence_score ?? ""}
                data-testid="rel-fcs" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-cocoa">Beziehungsstärke</span>
              <select className="wit-input" name="rs" defaultValue={investor.relationship_strength ?? ""} data-testid="rel-rs">
                <option value="">—</option>
                <option value="low">low</option><option value="medium">medium</option><option value="high">high</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-cocoa">Trust Level</span>
              <select className="wit-input" name="tl" defaultValue={investor.trust_level ?? ""} data-testid="rel-tl">
                <option value="">—</option>
                <option value="low">low</option><option value="medium">medium</option><option value="high">high</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-cocoa">Discretion Level</span>
              <select className="wit-input" name="dl" defaultValue={investor.discretion_level ?? ""} data-testid="rel-dl">
                <option value="">—</option>
                <option value="low">low</option><option value="medium">medium</option><option value="high">high</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-cocoa">Intro Readiness</span>
              <select className="wit-input" name="ir" defaultValue={investor.intro_readiness ?? ""} data-testid="rel-ir">
                <option value="">—</option>
                <option value="not_yet">not_yet</option><option value="warm">warm</option><option value="ready">ready</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-xs font-medium text-cocoa">Persönliche Notiz</span>
              <textarea className="wit-input resize-none" rows={4} name="pn" defaultValue={investor.personal_note ?? ""} data-testid="rel-pn" />
            </label>
            <div className="sm:col-span-2">
              <button type="submit" disabled={relSaving} className="wit-btn-primary disabled:opacity-60" data-testid="rel-save">
                {relSaving ? "Speichern …" : "Speichern"}
              </button>
            </div>
          </form>
        )}

        {tab === "consent" && (
          <div data-testid="tab-content-consent">
            {(!consents || consents.length === 0) && <p className="text-sm text-warmGrey">Keine Consent-Records.</p>}
            <ul className="divide-y divide-sandBeige/60">
              {consents.map((c) => (
                <li key={c.id} className="py-3">
                  <p className="text-sm text-cocoa">{c.consent_text}</p>
                  <p className="text-xs text-warmGrey mt-1">{c.consent_type} · {c.consent_version} · {c.language?.toUpperCase()} · {c.granted_at ? new Date(c.granted_at).toLocaleString("de-DE") : ""}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <StatusChangeModal
        open={statusOpen} onClose={() => setStatusOpen(false)}
        onSaved={(s) => setData((d) => ({ ...d, investor: { ...d.investor, status: s } }))}
        entityType="investor" entityId={id}
        currentStatus={investor.status}
        options={PIPELINE_INVESTORS}
      />
    </div>
  );
}

const Row = ({ label, value, link }) => (
  <div className="contents">
    <dt className="text-xs text-warmGrey">{label}</dt>
    <dd className="text-sm text-cocoa break-words">
      {link && value ? (
        <a href={value} target="_blank" rel="noreferrer" className="hover:text-raspberry">{value}</a>
      ) : (value || "—")}
    </dd>
  </div>
);

const Section = ({ title, children }) => {
  const arr = Array.isArray(children) ? children : [children];
  if (arr.filter(Boolean).length === 0) return null;
  return (
    <div>
      <p className="wit-eyebrow text-warmGrey mb-2">{title}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
};
